import i18n from '@/i18n';
import { format } from 'date-fns';
import { imageSpaceAPI } from '@/utils/api';
import {
  ensureDisplayImageUrls,
  isManagedStorageUrl,
  toManagedCanonicalUploadsUrl,
  warmDisplayCacheFromRecords,
} from '@/utils/imageDisplayAccess';
import { filterAllowedUploadFiles } from '@/utils/ipeImageUploadFormats';
import {
  prepareStudioCompressedUploadFiles,
  STUDIO_UPLOAD_MAX_EDGE_PX,
  STUDIO_UPLOAD_DIMENSION_EXCEEDED_MESSAGE,
} from '@/pages/ipe/workbench/utils/studioCompressedSourceUpload';
import { sha256HexFromFile } from '@/utils/imageContentHash';
import {
  UPLOAD_BATCH_CHUNK_SIZE,
  UPLOAD_CHUNK_CONCURRENCY,
  UPLOAD_COMPRESS_CONCURRENCY,
  mapWithUploadConcurrency,
  withUploadCompressSlot,
  withUploadSaveSlot,
} from '@/utils/globalUploadBudget';
import { assertUploadSessionLimit } from '@/utils/uploadSessionLimits';
import {
  findImageSpaceDuplicate,
  loadExistingImagesForDedupe,
  normalizeImageSpaceName,
} from '@/utils/imageSpaceDedupe';
import { enforceImageSpaceMountContract } from '@/pages/ipe/workbench/utils/imageSpaceMountContract';
import {
  collectImageSpaceIdbTempIdsForGc,
  isImageSpaceIdbAsyncEnabled,
  scheduleImageSpaceIdbBackup,
  scheduleImageSpaceIdbGc,
} from '@/pages/ipe/workbench/utils/imageSpaceIdbBackup';
import {
  OptimisticBlobRegistry,
  buildOptimisticImageSpaceItem,
  createOptimisticLocalId,
  isOptimisticPreviewEnabled,
  quickFilterUploadFiles,
  readOptimisticLocalId,
  stampOptimisticLocalId,
} from '@/pages/ipe/workbench/utils/imageSpaceOptimisticPreview';
import { UPLOAD_CONFIG } from '@/utils/uploadConfig';

const SAVE_CHUNK_SIZE = UPLOAD_BATCH_CHUNK_SIZE;
/** 入库 flush 并行批次数（与 multipart 分片并行对齐），避免压缩被单次 batchSave 卡死 */
const SAVE_FLUSH_CONCURRENCY = Math.max(1, UPLOAD_CHUNK_CONCURRENCY || 2);

async function readImageDimensions(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      const size = { width: bitmap.width, height: bitmap.height };
      bitmap.close?.();
      return size;
    } catch {
      /* fall through */
    }
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const size = await new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject(new Error(i18n.t('workbench:ipeUtilError.imageSizeReadFailed')));
      img.src = objectUrl;
    });
    return size;
  } finally {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch {
      /* ignore */
    }
  }
}

function createLocalImageId() {
  return `LIB-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

/** 上传回包已有 viewUrl 时先 warm；再对全部受管 URL 做一次 thumb 批量签（禁止 per-card） */
async function primeUploadedDisplayUrls(enrichedNewImages) {
  const list = Array.isArray(enrichedNewImages) ? enrichedNewImages : [];
  if (list.length === 0) return;
  warmDisplayCacheFromRecords(list);
  const urls = [];
  for (const item of list) {
    const raw = String(item?.url || item?.src || '').trim();
    if (!raw || raw.startsWith('data:') || raw.startsWith('blob:') || raw.startsWith('indexeddb://')) {
      continue;
    }
    if (!isManagedStorageUrl(raw)) continue;
    urls.push(toManagedCanonicalUploadsUrl(raw) || raw);
  }
  const unique = [...new Set(urls)];
  if (!unique.length) return;
  await ensureDisplayImageUrls(unique, { variant: 'thumb' });
}

async function dedupeUploadFiles(files, existingImages, contentHashes = null) {
  const fileHashPairs = await Promise.all(
    files.map(async (file, index) => {
      let hash = '';
      if (Array.isArray(contentHashes) && contentHashes[index]) {
        hash = String(contentHashes[index]);
      } else if (file?._contentHash) {
        hash = String(file._contentHash);
      } else {
        // 降级：主线程再读整文件（Worker 路径应已带 hash）
        hash = await sha256HexFromFile(file);
      }
      if (!hash) hash = `fallback-${file.name}-${file.size}-${file.lastModified}`;
      return { file, hash };
    }),
  );

  const seenBatchHashes = new Set();
  const seenBatchNames = new Set();
  const uniqueFileList = [];
  const hashesForUnique = [];
  let skippedDup = 0;

  for (const { file, hash } of fileHashPairs) {
    const duplicate = findImageSpaceDuplicate(existingImages, {
      contentHash: hash,
      fileName: file.name,
    });
    if (duplicate) {
      skippedDup += 1;
      continue;
    }
    const normalizedName = normalizeImageSpaceName(file.name);
    if (seenBatchHashes.has(hash) || (normalizedName && seenBatchNames.has(normalizedName))) {
      skippedDup += 1;
      continue;
    }
    seenBatchHashes.add(hash);
    if (normalizedName) seenBatchNames.add(normalizedName);
    uniqueFileList.push(file);
    hashesForUnique.push(hash);
  }

  return { uniqueFileList, hashesForUnique, skippedDup };
}

function createAsyncLock() {
  let tail = Promise.resolve();
  return function withLock(fn) {
    const run = tail.then(() => fn(), () => fn());
    tail = run.then(() => undefined, () => undefined);
    return run;
  };
}

/**
 * 流式流水线：有界并行压缩一次 → 攒 chunk 异步直传（skipCompress）→ 与压缩重叠。
 * 进度按「压缩 + 上传」双轨推进，避免每 20 张二次压缩停顿。
 */
async function processAndSaveStreaming(files, hashesForUnique, {
  uploadOriginSource,
  uploadFolderId,
  creatorPayload,
  shouldAbort,
  onProgress,
  onPhaseChange,
  chunkSize = SAVE_CHUNK_SIZE,
  compressConcurrency = UPLOAD_COMPRESS_CONCURRENCY,
  flushConcurrency = SAVE_FLUSH_CONCURRENCY,
  /** @type {Map<string, string>|null} optimistic localId → blobUrl（失败保留预览） */
  optimisticBlobById = null,
}) {
  const now = format(new Date(), 'yyyy-MM-dd HH:mm');
  const newImages = [];
  const toSave = [];
  const processedMeta = [];
  /** @type {Array<object>} */
  let pendingChunk = [];
  const flushPromises = [];
  const withChunkLock = createAsyncLock();
  let compressDone = 0;
  let uploadDone = 0;
  const saveChunkSize = Math.max(1, Number(chunkSize) || SAVE_CHUNK_SIZE);
  const compressLimit = Math.max(1, Number(compressConcurrency) || UPLOAD_COMPRESS_CONCURRENCY);
  const flushLimit = Math.max(1, Number(flushConcurrency) || SAVE_FLUSH_CONCURRENCY);

  let flushActive = 0;
  /** @type {Array<() => void>} */
  const flushWaiters = [];

  const reportProgress = () => {
    const visualDone = Math.round((compressDone + uploadDone) / 2);
    onProgress?.(Math.min(files.length, visualDone), files.length);
  };

  const acquireFlushSlot = () => new Promise((resolve) => {
    const tryAcquire = () => {
      if (flushActive < flushLimit) {
        flushActive += 1;
        resolve();
        return;
      }
      flushWaiters.push(tryAcquire);
    };
    tryAcquire();
  });

  const releaseFlushSlot = () => {
    flushActive = Math.max(0, flushActive - 1);
    const next = flushWaiters.shift();
    if (next) next();
  };

  const flushChunk = async (chunk) => {
    if (!chunk?.length) return;
    await acquireFlushSlot();
    onPhaseChange?.('save');
    try {
      await withUploadSaveSlot(async () => {
        try {
          const saveResult = await imageSpaceAPI.batchSave(chunk, {
            creatorPayload,
            dedupe: false,
            skipCompress: true,
          });
          const savedChunk = Array.isArray(saveResult?.images) ? saveResult.images : chunk;
          toSave.push(...savedChunk);
          uploadDone += chunk.length;
          reportProgress();
        } catch (error) {
          console.warn('图片空间批次入库失败，保留本机预览:', error);
          // T-A14/C：保留 uploadFile 便于内存重试；勿仅依赖尚未写完的 IDB
          toSave.push(...chunk.map((item) => ({
            ...item,
            url: '',
            ingestPending: true,
          })));
          uploadDone += chunk.length;
          reportProgress();
        }
      });
    } finally {
      releaseFlushSlot();
      if (flushActive === 0 && compressDone < files.length) {
        onPhaseChange?.('compress');
      }
    }
  };

  const scheduleFlushLocked = (force = false) => {
    if (!force && pendingChunk.length < saveChunkSize) return;
    if (pendingChunk.length === 0) return;
    const chunk = pendingChunk;
    pendingChunk = [];
    flushPromises.push(flushChunk(chunk));
  };

  onPhaseChange?.('compress');

  await mapWithUploadConcurrency(
    files,
    compressLimit,
    async (file, index) => {
      if (shouldAbort?.()) return;
      let uploadFile = null;
      let width = null;
      let height = null;
      let imageId = '';
      try {
        const prepared = await withUploadCompressSlot(async () => {
          const dims = await readImageDimensions(file);
          return { compressed: file, dims };
        });
        uploadFile = prepared.compressed;
        width = prepared.dims?.width || null;
        height = prepared.dims?.height || null;
        // T-A18：复用乐观卡 localId，避免 LIB 双卡
        imageId = readOptimisticLocalId(file) || readOptimisticLocalId(uploadFile) || createLocalImageId();
        if (hashesForUnique[index]) {
          /* contentHash applied below */
        }
      } catch (error) {
        console.warn(`图片 ${file?.name || index} 处理失败:`, error);
        compressDone += 1;
        reportProgress();
        return;
      }

      try {
        // T-A14：热路径携带 uploadFile 直传 OSS；IDB 默认异步备份，不阻塞 flush
        await scheduleImageSpaceIdbBackup(imageId, uploadFile, {
          async: isImageSpaceIdbAsyncEnabled(),
        });
        const contentHash = hashesForUnique[index] || null;
        const optimisticPreviewUrl = optimisticBlobById?.get?.(imageId) || null;
        const rawUploadName = String(uploadFile.name || file.name || '').trim();
        const displayUploadName = (() => {
          if (!rawUploadName) return '';
          const base = rawUploadName.includes('/') ? rawUploadName.split('/').pop() : rawUploadName;
          const dot = base.lastIndexOf('.');
          if (dot > 0 && dot < base.length - 1) return base.slice(0, dot);
          return base;
        })();
        const localMeta = {
          id: imageId,
          clientLocalId: imageId,
          name: displayUploadName || rawUploadName,
          createdAt: now,
          width,
          height,
          size: uploadFile.size || null,
          source: uploadOriginSource,
          usageCount: 0,
          useCount: 0,
          contentHash,
          folderId: uploadFolderId,
          ...creatorPayload,
        };
        const chunkItem = {
          id: imageId,
          clientLocalId: imageId,
          name: displayUploadName || rawUploadName,
          url: '',
          dataUrl: null,
          uploadFile,
          _preCompressed: true,
          createdAt: now,
          uploadedAt: now,
          width,
          height,
          size: uploadFile.size || null,
          source: uploadOriginSource,
          contentHash,
          folderId: uploadFolderId,
          ...creatorPayload,
        };
        await withChunkLock(() => {
          newImages.push(localMeta);
          processedMeta.push({
            id: imageId,
            // 失败保留乐观 blob；成功 enrich 会清 dataUrl
            dataUrl: optimisticPreviewUrl,
          });
          pendingChunk.push(chunkItem);
          scheduleFlushLocked(false);
        });
      } catch (error) {
        console.error(`保存图片 ${imageId} 失败:`, error);
      }
      compressDone += 1;
      reportProgress();
    },
  );

  await withChunkLock(() => {
    scheduleFlushLocked(true);
  });
  if (flushPromises.length > 0) {
    onPhaseChange?.('save');
    await Promise.all(flushPromises);
  }
  return { newImages, toSave, processedImages: processedMeta, now };
}

/**
 * 图片空间批量上传：校验 →（可选乐观入列）→ 流式压缩入库，支持进度与终止。
 */
export async function uploadImageSpaceFiles({
  files,
  existingImages = [],
  uploadFolderId = '',
  uploadOriginSource = 'upload',
  creatorPayload = {},
  shouldAbort = () => false,
  onPhaseChange,
  onProgress,
  /** T-A18：乐观卡入列 */
  onOptimisticMount = null,
  /** T-A18：内容重复 / 校验失败撤卡 */
  onOptimisticWithdraw = null,
  /** @visibleForTesting 覆盖 Flag */
  forceOptimisticPreview = null,
  /** @deprecated 仅测试/调优：覆盖默认 chunk / 并发 */
  pipelineOptions = null,
}) {
  const filtered = filterAllowedUploadFiles(files || []);
  let imageFiles = Array.isArray(filtered) ? filtered : [];
  if (imageFiles.length === 0) {
    return {
      ok: false,
      successCount: 0,
      total: 0,
      skippedDup: 0,
      message: i18n.t('workbench:studioToast.uploadImagesOnly'),
      level: 'info',
    };
  }

  const limitCheck = assertUploadSessionLimit(imageFiles.length, 0);
  if (!limitCheck.ok) {
    return {
      ok: false,
      successCount: 0,
      total: imageFiles.length,
      skippedDup: 0,
      message: limitCheck.message,
      level: 'warning',
    };
  }

  const optimisticOn = forceOptimisticPreview != null
    ? Boolean(forceOptimisticPreview)
    : isOptimisticPreviewEnabled();
  const blobRegistry = new OptimisticBlobRegistry();
  /** @type {string[]} */
  const mountedOptimisticIds = [];
  /** @type {string[]} */
  const withdrawnIds = [];
  /** @type {Map<string, string>} */
  const optimisticBlobById = new Map();

  const withdrawOptimistic = (ids) => {
    const list = (Array.isArray(ids) ? ids : []).map(String).filter(Boolean);
    if (!list.length) return;
    const unique = [...new Set(list)];
    onOptimisticWithdraw?.(unique);
    blobRegistry.revokeMany(unique);
    for (const id of unique) {
      optimisticBlobById.delete(id);
      withdrawnIds.push(id);
    }
  };

  let skippedQuick = 0;
  if (optimisticOn) {
    const quick = quickFilterUploadFiles(imageFiles, existingImages);
    skippedQuick = quick.skippedQuick;
    imageFiles = quick.accepted;
    if (imageFiles.length === 0) {
      return {
        ok: false,
        successCount: 0,
        total: (Array.isArray(filtered) ? filtered : []).length,
        skippedDup: skippedQuick,
        skippedQuick,
        message: i18n.t('workbench:ipeUtilError.duplicateImages'),
        level: 'info',
      };
    }

    const now = format(new Date(), 'yyyy-MM-dd HH:mm');
    const optimisticItems = [];
    for (const file of imageFiles) {
      const localId = createOptimisticLocalId();
      stampOptimisticLocalId(file, localId);
      let blobUrl = '';
      try {
        blobUrl = typeof URL !== 'undefined' && URL.createObjectURL
          ? URL.createObjectURL(file)
          : '';
      } catch {
        blobUrl = '';
      }
      if (blobUrl) {
        blobRegistry.register(localId, blobUrl);
        optimisticBlobById.set(localId, blobUrl);
      }
      mountedOptimisticIds.push(localId);
      optimisticItems.push(buildOptimisticImageSpaceItem(file, {
        id: localId,
        blobUrl,
        uploadFolderId,
        uploadOriginSource,
        creatorPayload,
        createdAt: now,
      }));
    }
    // 须在 await prepare 之前同步回调，保证「不等 Worker」见卡
    onOptimisticMount?.(optimisticItems);
  }

  onPhaseChange?.('compress');
  onProgress?.(0, imageFiles.length);

  const { files: preparedFiles, errors: prepareErrors, contentHashes: preparedHashes } =
    await prepareStudioCompressedUploadFiles(
    imageFiles,
    {
      maxEdgePx: STUDIO_UPLOAD_MAX_EDGE_PX,
      dimensionExceededMessage: STUDIO_UPLOAD_DIMENSION_EXCEEDED_MESSAGE,
    },
  );
  const rejectedValidation = Math.max(0, imageFiles.length - preparedFiles.length);
  const validationMessages = [...new Set(prepareErrors.filter(Boolean))];

  if (optimisticOn && rejectedValidation > 0) {
    const kept = new Set(
      preparedFiles.map((f) => readOptimisticLocalId(f)).filter(Boolean),
    );
    const failedIds = mountedOptimisticIds.filter((id) => !kept.has(id) && !withdrawnIds.includes(id));
    withdrawOptimistic(failedIds);
  }

  if (preparedFiles.length === 0) {
    if (optimisticOn) {
      withdrawOptimistic(mountedOptimisticIds.filter((id) => !withdrawnIds.includes(id)));
    }
    return {
      ok: false,
      successCount: 0,
      total: imageFiles.length,
      skippedDup: skippedQuick,
      skippedQuick,
      rejectedValidation,
      withdrawnIds: [...withdrawnIds],
      message: validationMessages[0] || i18n.t('workbench:ipeUtilError.noUploadableImages'),
      level: 'warning',
    };
  }

  onPhaseChange?.('dedupe');
  onProgress?.(0, preparedFiles.length);

  const mergedExisting = await loadExistingImagesForDedupe(existingImages);
  const { uniqueFileList, hashesForUnique, skippedDup } = await dedupeUploadFiles(
    preparedFiles,
    mergedExisting,
    preparedHashes,
  );

  if (optimisticOn) {
    const uniqueIds = new Set(
      uniqueFileList.map((f) => readOptimisticLocalId(f)).filter(Boolean),
    );
    const stillMounted = mountedOptimisticIds.filter((id) => !withdrawnIds.includes(id));
    const dupIds = stillMounted.filter((id) => !uniqueIds.has(id));
    withdrawOptimistic(dupIds);
  }

  const validationHint = rejectedValidation > 0
    ? i18n.t('workbench:ipeUtilError.skippedRejected', { count: rejectedValidation })
    : '';
  const validationDetailHint = validationMessages.length > 0 && rejectedValidation > 0
    ? validationMessages[0]
    : '';
  const totalSkippedDup = skippedDup + skippedQuick;

  if (uniqueFileList.length === 0) {
    if (optimisticOn) {
      withdrawOptimistic(mountedOptimisticIds.filter((id) => !withdrawnIds.includes(id)));
    }
    const dupOnly = totalSkippedDup > 0 && rejectedValidation === 0;
    return {
      ok: false,
      successCount: 0,
      total: preparedFiles.length,
      skippedDup: totalSkippedDup,
      skippedQuick,
      rejectedValidation,
      withdrawnIds: [...withdrawnIds],
      message: dupOnly
        ? i18n.t('workbench:ipeUtilError.duplicateImages')
        : (validationDetailHint || i18n.t('workbench:ipeUtilError.noUploadableImagesHint', { hint: validationHint })),
      level: 'info',
    };
  }

  if (shouldAbort()) {
    // 中止：保留已入列乐观卡（可重试）；不批量 revoke
    return {
      ok: false,
      successCount: 0,
      total: uniqueFileList.length,
      skippedDup: totalSkippedDup,
      skippedQuick,
      rejectedValidation,
      withdrawnIds: [...withdrawnIds],
      optimisticIds: mountedOptimisticIds.filter((id) => !withdrawnIds.includes(id)),
      message: i18n.t('workbench:ipeUtilError.uploadTerminated'),
      level: 'info',
      aborted: true,
    };
  }

  onPhaseChange?.('compress');
  onProgress?.(0, uniqueFileList.length);

  const { newImages, toSave, processedImages: savedProcessed } = await processAndSaveStreaming(
    uniqueFileList,
    hashesForUnique,
    {
      uploadOriginSource,
      uploadFolderId,
      creatorPayload,
      shouldAbort,
      onProgress,
      onPhaseChange,
      optimisticBlobById: optimisticOn ? optimisticBlobById : null,
      ...(pipelineOptions && typeof pipelineOptions === 'object' ? pipelineOptions : {}),
    },
  );

  const revokeSucceededBlobs = (enriched) => {
    if (!optimisticOn) return;
    for (const item of (Array.isArray(enriched) ? enriched : [])) {
      if (!item || item.ingestPending === true) continue;
      const localId = item.clientLocalId != null && String(item.clientLocalId) !== ''
        ? String(item.clientLocalId)
        : (String(item.id || '').startsWith('LIB-') ? String(item.id) : '');
      if (localId && blobRegistry.has(localId)) {
        blobRegistry.revokeOne(localId);
        optimisticBlobById.delete(localId);
      }
    }
  };

  if (shouldAbort()) {
    const enrichedNewImages = finalizeEnrichedNewImages(newImages, toSave, savedProcessed);
    await primeUploadedDisplayUrls(enrichedNewImages);
    scheduleImageSpaceIdbGc(collectImageSpaceIdbTempIdsForGc(enrichedNewImages));
    revokeSucceededBlobs(enrichedNewImages);
    return {
      ok: toSave.length > 0,
      successCount: enrichedNewImages.length,
      total: uniqueFileList.length,
      skippedDup: totalSkippedDup,
      skippedQuick,
      rejectedValidation,
      withdrawnIds: [...withdrawnIds],
      message: toSave.length > 0
        ? i18n.t('workbench:ipeUtilError.uploadTerminatedPartial', { success: enrichedNewImages.length, total: uniqueFileList.length, hint: validationHint })
        : i18n.t('workbench:ipeUtilError.uploadTerminated'),
      level: toSave.length > 0 ? 'warning' : 'info',
      aborted: true,
      enrichedNewImages,
    };
  }

  if (newImages.length === 0) {
    return {
      ok: false,
      successCount: 0,
      total: uniqueFileList.length,
      skippedDup: totalSkippedDup,
      skippedQuick,
      rejectedValidation,
      withdrawnIds: [...withdrawnIds],
      message: validationDetailHint || i18n.t('workbench:ipeUtilError.imageProcessFailedRetry', { hint: validationHint }),
      level: 'error',
    };
  }

  const enrichedNewImages = finalizeEnrichedNewImages(newImages, toSave, savedProcessed);
  await primeUploadedDisplayUrls(enrichedNewImages);
  // T-A14/T-A19：成功项清理 LIB-* 临时 IDB 键（异步写可能晚到，GC 可幂等）
  scheduleImageSpaceIdbGc(collectImageSpaceIdbTempIdsForGc(enrichedNewImages));
  revokeSucceededBlobs(enrichedNewImages);
  const dupHint = totalSkippedDup > 0 ? i18n.t('workbench:ipeUtilError.skippedDuplicate', { count: totalSkippedDup }) : '';
  const rejectSuffix = validationHint || '';

  let message;
  let level = 'success';
  if (enrichedNewImages.length === 0) {
    message = i18n.t('workbench:ipeUtilError.writeEntriesFailed', { hint: `${dupHint}${rejectSuffix}` });
    level = 'warning';
  } else if (toSave.length === 0) {
    message = i18n.t('workbench:ipeUtilError.serverWriteLocalOnly', { hint: `${dupHint}${rejectSuffix}` });
    level = 'warning';
  } else if (enrichedNewImages.length < newImages.length) {
    message = i18n.t('workbench:ipeUtilError.savedToListPartial', { saved: enrichedNewImages.length, total: newImages.length, hint: `${dupHint}${rejectSuffix}` });
    level = 'warning';
  } else if (toSave.length < newImages.length) {
    message = i18n.t('workbench:ipeUtilError.savedToServerPartial', { saved: toSave.length, total: newImages.length, hint: `${dupHint}${rejectSuffix}` });
    level = 'warning';
  } else {
    message = i18n.t('workbench:ipeUtilError.uploadedImagesSuccess', { count: enrichedNewImages.length, hint: `${dupHint}${rejectSuffix}` });
    if (rejectedValidation > 0 && validationDetailHint) {
      level = 'warning';
    }
  }

  return {
    ok: enrichedNewImages.length > 0,
    successCount: enrichedNewImages.length,
    savedCount: toSave.length,
    total: uniqueFileList.length,
    skippedDup: totalSkippedDup,
    skippedQuick,
    rejectedValidation,
    withdrawnIds: [...withdrawnIds],
    message,
    level,
    enrichedNewImages,
  };
}

/** M1：enrich 后强制挂载契约，再入列 / 计成功数 */
export function finalizeEnrichedNewImages(newImages, toSave, processedImages) {
  const enriched = buildEnrichedNewImages(newImages, toSave, processedImages);
  const mountOpts = { mountStrict: UPLOAD_CONFIG.mountStrict !== false };
  const { accepted } = enforceImageSpaceMountContract(enriched, mountOpts);
  if (accepted.length > 0) return accepted;
  // enrich 按 LIB id 对不上、但 toSave 已有受管 url 时，仍应用服务端行入列，避免弹窗不关/列表空白
  const saved = Array.isArray(toSave) ? toSave : [];
  if (saved.length === 0) return accepted;
  const { accepted: fromSaved } = enforceImageSpaceMountContract(saved, mountOpts);
  return fromSaved;
}

export function buildEnrichedNewImages(newImages, toSave, processedImages) {
  const savedById = new Map();
  const savedByLocalId = new Map();
  for (const item of (Array.isArray(toSave) ? toSave : [])) {
    if (!item || item.id == null || item.id === '') continue;
    const id = String(item.id);
    savedById.set(id, item);
    const localId = item.clientLocalId != null && item.clientLocalId !== ''
      ? String(item.clientLocalId)
      : '';
    if (localId) savedByLocalId.set(localId, item);
  }
  const procById = new Map(
    (Array.isArray(processedImages) ? processedImages : []).map((item) => [String(item.id), item]),
  );
  return (Array.isArray(newImages) ? newImages : []).map((img) => {
    const imgId = img?.id != null ? String(img.id) : '';
    const saved = (imgId && (savedById.get(imgId) || savedByLocalId.get(imgId))) || null;
    const proc = (imgId && procById.get(imgId))
      || (saved?.clientLocalId ? procById.get(String(saved.clientLocalId)) : null)
      || null;
    const persistedUrl = saved?.url && !String(saved.url).startsWith('data:')
      ? saved.url
      : '';
    const dataUrl = proc?.dataUrl ?? saved?.dataUrl ?? img.dataUrl ?? null;
    const ingestPending = saved?.ingestPending === true
      || (!persistedUrl && Boolean(dataUrl));
    const serverId = saved?.id != null && saved.id !== '' ? String(saved.id) : imgId;
    const clientLocalId = saved?.clientLocalId != null && String(saved.clientLocalId) !== serverId
      ? String(saved.clientLocalId)
      : (serverId && imgId && serverId !== imgId ? imgId : undefined);
    return {
      ...img,
      id: serverId || img.id,
      ...(clientLocalId ? { clientLocalId } : {}),
      name: (() => {
        const localName = String(img?.name || '').trim();
        const savedName = String(saved?.name || '').trim();
        const recordId = String(serverId || imgId || '').trim();
        const stripExt = (name) => {
          const raw = String(name || '').trim();
          if (!raw) return '';
          const base = raw.includes('/') ? raw.split('/').pop() : raw;
          const dot = base.lastIndexOf('.');
          if (dot > 0 && dot < base.length - 1) return base.slice(0, dot);
          return base;
        };
        const opaque = (name) => {
          const base = stripExt(name);
          if (!base) return true;
          if (recordId && base.toLowerCase() === stripExt(recordId).toLowerCase()) return true;
          return /^img-[a-f0-9]{16,}$/i.test(base)
            || /^[a-f0-9]{32}$/i.test(base)
            || /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(base);
        };
        let picked = '';
        if (localName && !opaque(localName)) picked = localName;
        else if (savedName && !opaque(savedName)) picked = savedName;
        else picked = localName || savedName || '';
        return stripExt(picked) || picked;
      })(),
      url: persistedUrl,
      viewUrl: saved?.viewUrl || saved?.storedViewUrl || '',
      displayUrl: saved?.displayUrl || saved?.viewUrl || saved?.storedUrlDisplay || '',
      dataUrl: ingestPending ? dataUrl : null,
      ingestPending,
    };
  });
}
